import { HttpErrorResponse } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  computed,
  inject,
  signal,
} from '@angular/core';
import { Location } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

import { ErrorStateComponent } from '../../../ui/error-state/error-state.component';
import {
  QuestionEditorComponent,
  QuestionEditorValue,
} from '../question-editor/question-editor.component';
import { toCreateDto } from '../question-editor/question-editor.mapper';

import { LearningPath } from '../../../../core/interfaces/learning-path';
import { Subject as SubjectDto } from '../../../../core/interfaces/subject';
import { Theme } from '../../../../core/interfaces/theme';
import { LearningService } from '../../../../core/services/learning-path.service';
import { QuestionService } from '../../../../core/services/question.service';
import { SwalService } from '../../../../core/services/utils/swal.service';

/** Étape du parcours de création. */
type Step = 'scope' | 'editor' | 'saved';

/**
 * Création d'une question, en page autonome.
 *
 * <p>La page choisit elle-même sa portée — niveau, matière, chapitre — plutôt
 * que de l'hériter d'un écran appelant. Une question appartient à un chapitre :
 * la lui faire désigner explicitement évite les rattachements involontaires que
 * produisait l'ancienne modale, où le chapitre venait d'un filtre laissé
 * derrière soi.</p>
 *
 * <p>Après enregistrement, l'écran ne renvoie pas ailleurs : il propose
 * d'enchaîner. Saisir des questions se fait par séries — repartir du même
 * chapitre est le cas courant, et refaire trois sélections entre chaque item
 * décourage la saisie.</p>
 */
@Component({
  selector: 'app-question-create',
  standalone: true,
  imports: [TranslateModule, ErrorStateComponent, QuestionEditorComponent],
  templateUrl: './question-create.component.html',
  styleUrl: './question-create.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class QuestionCreateComponent implements OnInit {
  private readonly learning = inject(LearningService);
  private readonly questions = inject(QuestionService);
  private readonly swal = inject(SwalService);
  private readonly translate = inject(TranslateService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly location = inject(Location);

  readonly step = signal<Step>('scope');

  /* ── Portée ────────────────────────────────────────────── */

  readonly paths = signal<LearningPath[]>([]);
  readonly subjects = signal<SubjectDto[]>([]);
  readonly themes = signal<Theme[]>([]);

  readonly pathId = signal<number | null>(null);
  readonly subjectId = signal<number | null>(null);
  readonly themeId = signal<number | null>(null);

  readonly loadingScope = signal(false);
  readonly scopeErrored = signal(false);

  readonly saving = signal(false);

  /** Nombre de questions créées dans cette session de saisie. */
  readonly createdCount = signal(0);

  /** Titre de la dernière question enregistrée, pour la confirmation. */
  readonly lastTitle = signal('');

  readonly canStart = computed(() => this.themeId() !== null);

  readonly scopeLabel = computed(() => {
    const path = this.paths().find((p) => p.id === this.pathId());
    const subject = this.subjects().find((s) => s.id === this.subjectId());
    const theme = this.themes().find((t) => t.id === this.themeId());
    return [path?.title, subject?.name, theme?.title].filter(Boolean).join(' · ');
  });

  /**
   * Écran d'où l'on vient, pour y revenir en fin de série. Absent quand la page
   * est ouverte directement : on retombe alors sur la liste des tests.
   */
  private returnUrl: string | null = null;

  ngOnInit(): void {
    this.returnUrl = this.route.snapshot.queryParamMap.get('returnUrl');
    this.loadPaths();
  }

  /* ── Chargement de la portée ───────────────────────────── */

  loadPaths(): void {
    this.loadingScope.set(true);
    this.scopeErrored.set(false);

    this.learning.getLearningPaths(true, false).subscribe({
      next: (res) => {
        this.paths.set(res?.data ?? []);
        this.loadingScope.set(false);
      },
      error: (_: HttpErrorResponse) => {
        this.paths.set([]);
        this.scopeErrored.set(true);
        this.loadingScope.set(false);
      },
    });
  }

  onPath(raw: string): void {
    const id = Number(raw) || null;
    this.pathId.set(id);
    // Changer de niveau invalide la matière et le chapitre : les garder
    // laisserait une portée incohérente.
    this.subjectId.set(null);
    this.themeId.set(null);
    this.subjects.set([]);
    this.themes.set([]);

    if (!id) return;

    this.learning.getSubjectsByLearningPath(id, false).subscribe({
      next: (res) => this.subjects.set(res?.data ?? []),
      error: () => this.subjects.set([]),
    });
  }

  onSubject(raw: string): void {
    const id = Number(raw) || null;
    this.subjectId.set(id);
    this.themeId.set(null);
    this.themes.set([]);

    if (!id) return;

    this.learning.getThemesBySubject(id).subscribe({
      next: (res) => this.themes.set(res?.data ?? []),
      error: () => this.themes.set([]),
    });
  }

  onTheme(raw: string): void {
    this.themeId.set(Number(raw) || null);
  }

  start(): void {
    if (this.canStart()) this.step.set('editor');
  }

  /* ── Enregistrement ────────────────────────────────────── */

  onSave(value: QuestionEditorValue): void {
    const themeId = this.themeId();
    if (!themeId || this.saving()) return;

    this.saving.set(true);

    this.questions.create(toCreateDto(value, themeId)).subscribe({
      next: () => {
        this.saving.set(false);
        this.createdCount.update((n) => n + 1);
        this.lastTitle.set(value.title);
        // On ne quitte pas l'écran : la suite est un choix de l'utilisateur.
        this.step.set('saved');
      },
      error: () => {
        // L'intercepteur global a déjà signalé l'échec ; la saisie reste
        // intacte pour que rien ne soit perdu.
        this.saving.set(false);
      },
    });
  }

  /* ── Après enregistrement ──────────────────────────────── */

  /** Enchaîner dans le même chapitre — le cas le plus courant. */
  againSameChapter(): void {
    this.step.set('editor');
  }

  /** Enchaîner ailleurs : on revient au choix de la portée. */
  againOtherChapter(): void {
    this.step.set('scope');
  }

  finish(): void {
    const count = this.createdCount();
    if (count > 0) {
      this.swal.success(
        this.translate.instant('QUESTION_CREATE.DONE_TOAST', { count }),
      );
    }

    if (this.returnUrl) {
      this.router.navigateByUrl(this.returnUrl);
    } else {
      this.location.back();
    }
  }

  cancel(): void {
    if (this.returnUrl) this.router.navigateByUrl(this.returnUrl);
    else this.location.back();
  }
}
